/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import useEvent from 'react-use-event-hook';
import { getCommonCodeNames } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { ItemStatCd, MpItem, MpItemTpCd } from 'models/mp/MpItem';
import {
  ProcessBarWrap,
  ProcessStepBox,
  StepBox,
  ProcessStep,
  ProcessNum,
  ProcessText,
  ProgressBar,
  ProgressBarWrapper,
  StatusBox,
  StatusText,
} from 'components/process/Process';
import { findMpItemStatHis } from 'apis/mp/MpItem';

interface Props {
  mpItemId?: string; // item ID
}

type StatusType = 'status_1' | 'status_2' | 'status_3';
interface StepItem {
  num: number;
  text: string;
  itemTpCd: string;
  status: StatusType;
  visual?: 'done' | 'now';
}

const MpItemStatProcess = ({ mpItemId }: Props, ref) => {
  const statusWrapperRef = useRef<HTMLDivElement>(null);
  const [code, setCode] = useState<any>();
  const [includeItemTpCd, setIncludeItemTpCd] = useState<MpItemTpCd[]>();
  const [stepData, setStepData] = useState<StepItem[]>();
  const [widths, setWidths] = useState<any>();
  const [rowDataMpItemStatHis, setRowDataMpItemStatHis] = useState<MpItem[]>([]);

  useImperativeHandle(ref, () => ({
    refresh: (mpItemId) => {
      searchMpItemStatHis(mpItemId);
    },
  }));

  useEffect(() => {
    searchMpItemStatHis(mpItemId);
  }, [mpItemId]);

  useEffect(() => {
    const mpItem = (rowDataMpItemStatHis || []).length > 0 ? rowDataMpItemStatHis[0] : undefined;
    const isDropChk = mpItem?.itemStatCd === ItemStatCd.Drop; // 현재 DROP
    const isCmplChk = mpItem?.itemStatCd === ItemStatCd.Complete; // 현채 Complete
    const isDe = (rowDataMpItemStatHis || []).findIndex((i) => i.itemStatCd === ItemStatCd.HeHold); // 수평전개보류

    const includeItemTpCd = [MpItemTpCd.NI];
    if (mpItem?.fstrYn === 'Y') {
      includeItemTpCd.push(MpItemTpCd.MPHE);
    } else {
      includeItemTpCd.push(MpItemTpCd.MP, MpItemTpCd.HE);
    }

    let filteredSteps = (code?.mpItemStatMap || []).filter(
      (o) => includeItemTpCd.includes(o.optValCtn1 || '') || (!isCmplChk && o.cmnCd === 'END')
    );
    if (isDe < 0) {
      filteredSteps = filteredSteps.filter((o) => o.optValCtn2 !== ItemStatCd.HeHold);
    }

    if (isDropChk || isCmplChk) {
      const prevMpItem =
        (rowDataMpItemStatHis || []).length > 1 ? rowDataMpItemStatHis[1] : undefined;
      if (isDropChk || isDe > 0) {
        const dropAddIdx = (code?.mpItemStatMap || []).findIndex(
          (o) => o.optValCtn1 === prevMpItem?.mpItemTpCd && o.optValCtn2 === prevMpItem?.itemStatCd
        );
        if (dropAddIdx !== -1) {
          filteredSteps = (code?.mpItemStatMap || []).slice(0, dropAddIdx + 1);
        }
      }
      let endCode = (code?.mpItemStatMap || []).filter((o) => o.cmnCd === 'END');
      endCode = endCode.length > 0 ? endCode[0] : undefined;

      filteredSteps = [
        ...filteredSteps,
        {
          cmnCd: isDropChk ? 'DROP' : endCode?.cmnCd || 'END',
          cmnCdNm: isDropChk ? 'DROP' : endCode?.cmnCdNm || '완료',
          cmnCdDesc: '',
          optValCtn1: mpItem?.mpItemTpCd,
          optValCtn2: mpItem?.itemStatCd,
        },
      ];
    }

    let currentStepNo = -1;
    const stepData = (filteredSteps || []).reduce((acc, cur, idx) => {
      const isNow = mpItem?.itemStatCd === cur.optValCtn2 && mpItem?.mpItemTpCd === cur.optValCtn1;
      if (isNow) currentStepNo = idx;
      const index = includeItemTpCd.findIndex((o) => o === cur.optValCtn1 || '');
      acc.push({
        num: idx + 1,
        text: cur.cmnCdNm,
        itemTpCd: cur.optValCtn1 || includeItemTpCd[includeItemTpCd.length - 1],
        status: `status_${index > -1 ? index + 1 : includeItemTpCd.length}`,
        visual: isNow
          ? 'now'
          : Object.keys(rowDataMpItemStatHis || {}).length > 0 && currentStepNo < 0
          ? 'done'
          : 'disable',
      });
      return acc;
    }, [] as StepItem[]);

    setIncludeItemTpCd(includeItemTpCd);
    setStepData(stepData);
  }, [code?.mpItemStatMap, rowDataMpItemStatHis]);

  const calcWidths = useEvent(() => {
    if (statusWrapperRef.current) {
      const steps = statusWrapperRef.current.querySelectorAll<HTMLDivElement>('.process-step');

      const newWidths = (includeItemTpCd || []).reduce(
        (acc, cur) => ({
          ...acc,
          [cur]: 0,
        }),
        {}
      );

      steps.forEach((step) => {
        const width = step.offsetWidth;
        if (step.classList.contains(MpItemTpCd.NI)) newWidths[MpItemTpCd.NI] += width;
        else if (step.classList.contains(MpItemTpCd.MP)) newWidths[MpItemTpCd.MP] += width;
        else if (step.classList.contains(MpItemTpCd.HE)) newWidths[MpItemTpCd.HE] += width;
        else if (step.classList.contains(MpItemTpCd.MPHE)) newWidths[MpItemTpCd.MPHE] += width;
      });

      setWidths(newWidths);
    }
  });

  useEffect(() => {
    calcWidths();
  }, [includeItemTpCd, stepData]);

  useEffect(() => {
    getCommonCodes();

    if (!statusWrapperRef.current) return;

    window.addEventListener('resize', calcWidths);

    // 사이드바 토글 이벤트 핸들러
    const handleSidebarToggle = () => {
      // requestAnimationFrame을 사용하여 다음 애니메이션 프레임에 실행
      // 이렇게 하면 DOM 업데이트가 완료된 후에 정확한 width 값을 측정할 수 있습니다.
      requestAnimationFrame(() => {
        calcWidths();
      });
    };

    window.addEventListener('sidebar-toggle-start', handleSidebarToggle);
    window.addEventListener('sidebar-toggle-end', handleSidebarToggle);

    const resizeObserver = new ResizeObserver(() => {
      calcWidths();
    });

    resizeObserver.observe(statusWrapperRef.current);

    return () => {
      window.removeEventListener('resize', calcWidths);
      window.removeEventListener('sidebar-toggle-start', handleSidebarToggle);
      window.removeEventListener('sidebar-toggle-end', handleSidebarToggle);
      resizeObserver.disconnect();
    };
  }, []);

  const searchMpItemStatHis = (mpItemId) => {
    if (mpItemId) {
      findMpItemStatHis(mpItemId).then((result) => {
        setRowDataMpItemStatHis(result);
      });
    } else {
      setRowDataMpItemStatHis([]);
    }
  };

  const getCommonCodes = async () => {
    const mpItemTpCd: Code[] = await getCommonCodeNames('MP_ITEM_TP_CD');
    const mpItemStatMap: Code[] = await getCommonCodeNames('MP_ITEM_STAT_MAP');
    setCode({
      mpItemTpCd: mpItemTpCd,
      mpItemStatMap: mpItemStatMap,
    });
  };

  const getItemTpCdNm = (itemTpCd) => {
    const t = (code?.mpItemTpCd || []).filter((o) => o.cmnCd === itemTpCd);
    if (t.length > 0) {
      return t[0].cmnCdNm;
    }
    return itemTpCd;
  };

  return (
    <>
      <ProcessBarWrap className="mgT" ref={statusWrapperRef}>
        {(stepData || []).map((step, idx) => (
          <ProcessStepBox
            key={idx}
            className={`process-step ${step.itemTpCd} ${step.status} ${
              step.visual ? `${step.visual}` : ''
            }`}
          >
            <StepBox>
              <ProcessStep>
                <ProcessNum>{step.num}</ProcessNum>
                <ProcessText>{step.text}</ProcessText>
              </ProcessStep>
            </StepBox>
          </ProcessStepBox>
        ))}
        <ProgressBar></ProgressBar>
        <ProgressBarWrapper>
          {(includeItemTpCd || []).map((itemTpCd, idx) =>
            widths[itemTpCd] > 0 ? (
              <StatusBox
                key={itemTpCd}
                className={`status_${idx + 1}`}
                style={{ width: `${widths[itemTpCd]}px` }}
              >
                <StatusText>{getItemTpCdNm(itemTpCd)}</StatusText>
              </StatusBox>
            ) : null
          )}
        </ProgressBarWrapper>
      </ProcessBarWrap>
    </>
  );
};

export default React.forwardRef(MpItemStatProcess);
